import type { ApiError, BrokerAccount, BrokerAccountChange } from '@ctcells/api'
import type { OperationId } from '@ctcells/contract'
import { canCallOperation } from '@ctcells/core'
import { errorMessage, hasMessage, t } from '@ctcells/i18n'

import type { ActionGate, ScreenAccess } from '../access.ts'

/**
 * 브로커 계정 · 모듈 브로커 화면의 판정 — 게이트 · 거절 문면 · 라벨. 서버 가드(`views/broker_accounts.py`)와 같은 차례다.
 *
 * | 차례 | 서버 가드 | 거절 | 화면 |
 * | --- | --- | --- | --- |
 * | 0 | — | — | 구독 stale → 「연결 끊김 — 제어 불가」(§6 · 상태를 바꾸는 단추다) |
 * | 1 | `require_roles({admin})` | 403 `INSUFFICIENT_ROLE` | 권한 없음(`/auth/me` `operations` 가 이긴다 · 모르면 `settings`) |
 * | 2 | `_protected` — 코어(`l2`) · 관리(`admin`) · 시험(`test`) 계정 | 409 `protected_account` | 비활성 + 사유. 🔴 **코어 자기 비밀번호만 예외**(`self_password`) |
 * | 3 | `write_refusal` — 시작을 막는 상태의 배치 | 409 `batch_active` | 목록의 `blocking_batch_id` 가 있으면 비활성 + 그 작업(07 §5.8) |
 * | 4 | 같은 계정 교체 진행 중 · 모듈 무응답 | 409 `change_in_progress` · `module_not_acknowledged` | **막지 않는다** — 화면이 모르는 판정이라 서버 문면이 선다 |
 *
 * 🔴 제어락을 보지 않는다 — 서버가 요구하지 않는다(경로 매개도 `name` · `id` 하나).
 * ⚠ 차례 2 · 3 의 재료는 서버 값(`kind` · `test` · `blocking_batch_id` · `BrokerCoreStatus.account`)이다. 화면이 지어내지 않는다.
 */

export type BrokerAction = 'roles' | 'disable' | 'enable' | 'password' | 'delete'

/** 동작 → 계약 연산 이름(`/auth/me` `operations` 대조) */
export const BROKER_OPERATIONS: Readonly<Record<BrokerAction, OperationId>> = {
  roles: 'setBrokerAccountRoles',
  disable: 'disableBrokerAccount',
  enable: 'enableBrokerAccount',
  password: 'changeBrokerPassword',
  delete: 'deleteBrokerAccount',
}

export const MODULE_BROKER_READ = 'getModuleBrokerConnection' satisfies OperationId
export const BROKER_LIST = 'listBrokerAccounts' satisfies OperationId

/** 권한 판정 — `operations` 를 알면 그것, 모르면(`null`) `settings`(admin 만 가진다 · 서버 `ROLES = {admin}`) */
export function canBroker(
  access: ScreenAccess,
  operations: readonly string[] | null,
  operation: OperationId,
): boolean {
  return operations === null
    ? (access.permissions?.includes('settings') ?? false)
    : canCallOperation(operations, operation)
}

/** 권한 · stale · 보내는 중 — 계정과 무관한 공통 앞단 */
export function brokerBaseGate(input: {
  access: ScreenAccess
  operations: readonly string[] | null
  operation: OperationId
  sending?: boolean
}): ActionGate {
  if (input.access.controlDisabled === true)
    return { disabled: true, disabledReason: t('reason.streamStale') }
  if (!canBroker(input.access, input.operations, input.operation))
    return { disabled: true, disabledReason: t('reason.noPermission', { perm: 'settings' }) }
  if (input.sending === true) return { disabled: true, disabledReason: t('reason.requestInFlight') }
  return { disabled: false }
}

/**
 * 계정 한 줄의 동작 게이트.
 *
 * @param coreAccount `GET /broker/core` 의 `account` — 코어 자기 비밀번호를 가른다. 모르면 `undefined`(그 한 줄만 막는다)
 */
export function brokerAccountGate(input: {
  access: ScreenAccess
  operations: readonly string[] | null
  account: BrokerAccount
  action: BrokerAction
  coreAccount?: string | null | undefined
  sending?: boolean
}): ActionGate {
  const { account, action } = input
  const base = brokerBaseGate({ ...input, operation: BROKER_OPERATIONS[action] })
  if (base.disabled) return base
  if (account.test === true)
    return { disabled: true, disabledReason: t('broker.reason.protected_test') }
  if (account.kind === 'admin')
    return { disabled: true, disabledReason: t('broker.reason.protected_admin') }
  if (account.kind === 'l2') {
    // 🔴 서버가 받는 단 하나 — 코어가 지금 쓰는 L2 계정의 비밀번호(`_writable(self_password=True)`)
    if (action !== 'password')
      return { disabled: true, disabledReason: t('broker.reason.protected_l2') }
    if (input.coreAccount === undefined)
      return { disabled: true, disabledReason: t('broker.reason.coreUnknown') }
    if (input.coreAccount !== account.name)
      return { disabled: true, disabledReason: t('broker.reason.protected_l2Other') }
  }
  if (action === 'disable' && account.disabled)
    return { disabled: true, disabledReason: t('broker.reason.alreadyDisabled') }
  if (action === 'enable' && !account.disabled)
    return { disabled: true, disabledReason: t('broker.reason.alreadyEnabled') }
  if (account.blocking_batch_id != null)
    return {
      disabled: true,
      disabledReason: t('broker.reason.batch_active', { batch: account.blocking_batch_id }),
    }
  return { disabled: false }
}

/**
 * 브로커 제어 실패의 분류 — 서버 503 문장 `브로커 제어 <kind>`(`views/broker_accounts.py` `_control_failed` ·
 * kind ∈ `unconfigured` · `unreachable` · `timeout`). 아니면 `undefined`.
 * ⚠ 서버가 이 분류를 코드로 주지 않고 문장 꼬리로만 준다 — 문장이 바뀌면 여기서 빠져 서버 문장이 그대로 선다(틀리지 않고 덜 친절해질 뿐).
 */
export function brokerControlKind(
  error: Pick<ApiError, 'status' | 'message'> | null | undefined,
): 'unconfigured' | 'unreachable' | 'timeout' | undefined {
  if (error?.status !== 503) return undefined
  const kind = /^브로커 제어 (\w+)$/.exec(error.message.trim())?.[1]
  return kind === 'unconfigured' || kind === 'unreachable' || kind === 'timeout' ? kind : undefined
}

/**
 * 거절 → 한 줄 문면 + 코드 원문.
 * 🔴 `BROKER_ACCOUNT_CHANGE_REJECTED` · `MODULE_REGISTRATION_REJECTED` · `REQUEST_VALIDATION_FAIL` 은 갈래를 `context.reason` 이
 *    가른다 — 갈래 문면을 사전에서 고르고, 사전에 없는 갈래는 서버 문면(`errorMessage`)으로 떨어진다(지어내지 않는다).
 * ⚠ 404 는 일반 봉투(`NOT_FOUND` · 영문 「Not Found」)라 여기서 「없다」로 옮긴다.
 * ⚠ 503 은 서버 한글 문장이 그대로 선다(「브로커 제어 unconfigured」 · 「닿지 못했다 — …」) — 바뀌었는지 모른다는 뜻이 그 안에 있다.
 */
export function brokerFailure(
  error: Pick<ApiError, 'error_code' | 'status' | 'context' | 'message'>,
): { text: string; code: string | undefined } {
  const rawReason: unknown = error.context?.['reason']
  const rawBatch: unknown = error.context?.['batch_id']
  const reason = typeof rawReason === 'string' ? rawReason : undefined
  const batch = typeof rawBatch === 'string' ? rawBatch : undefined
  const key = reason === undefined ? undefined : `broker.reason.${reason}`
  const control = brokerControlKind(error)
  const text =
    control !== undefined
      ? t(`broker.reason.control_${control}`)
      : error.status === 404
        ? t('broker.reason.not_found')
        : key !== undefined && hasMessage(key)
          ? t(key, { batch: batch ?? '—' })
          : errorMessage(error)
  return {
    text,
    code:
      error.error_code === undefined
        ? String(error.status)
        : reason === undefined
          ? error.error_code
          : `${error.error_code} · ${reason}`,
  }
}

/** 계정 종류 라벨 — 계약 enum 그대로(`module` · `l2` · `admin`) */
export function brokerKindLabel(kind: BrokerAccount['kind']): string {
  return t(`broker.kind.${kind}`)
}

/** 사본 일치 — `null`(사본 없음)은 «모름»이다. 「일치」로 접지 않는다 */
export function brokerSyncLabel(inSync: boolean | null | undefined): string {
  if (inSync === true) return t('broker.sync.ok')
  if (inSync === false) return t('broker.sync.differs')
  return t('broker.sync.unknown')
}

/** 감사 조작 이름 → 한글 · 사전에 없으면 원문(서버 어휘가 늘면 조용히 빠지지 않게) */
export function brokerOperationLabel(operation: string | null | undefined): string {
  if (operation == null || operation === '') return t('mask.none')
  const key = `broker.op.${operation}`
  return hasMessage(key) ? t(key) : operation
}

/** 교체 행 상태 → 한글 · 계약 enum 그대로 */
export function brokerChangeStateLabel(state: BrokerAccountChange['state']): string {
  return t(`broker.changeState.${state}`)
}

/**
 * 역할 고르기 후보 — 등록된 모듈 ID ∪ 지금 붙은 역할. 🔴 붙어 있는데 레지스트리에 없는 ID 도 후보에 남긴다 —
 * 빠뜨리면 다음 저장에서 그 역할이 조용히 떨어진다(PUT 이 전체 교체다).
 */
export function roleCandidates(
  registered: readonly string[],
  attached: readonly string[],
): string[] {
  return [...new Set([...attached, ...registered])].sort()
}

/** 두 집합이 같은가(순서 무관) — 바뀐 것 없이 보내지 않는다 */
export function sameModuleSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false
  const left = [...a].sort()
  const right = [...b].sort()
  return left.every((value, index) => value === right[index])
}
